import { toneOf } from "./crmHelpers";

/*
  Peças visuais pequenas usadas pelas telas do CRM.

  Ficam juntas num arquivo porque cada uma tem poucas linhas e nenhuma faz
  sentido sozinha — um arquivo por componente aqui só espalharia import.
*/

export const Badge = ({ tone = "neutral", children, className = "" }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
      toneOf(tone).badge
    } ${className}`}
  >
    {children}
  </span>
);

export const Dot = ({ tone = "neutral" }) => (
  <span className={`inline-block h-2 w-2 rounded-full ${toneOf(tone).dot}`} />
);

export const SectionTitle = ({ title, hint, action }) => (
  <div className="mb-3 flex items-end justify-between gap-4">
    <div className="min-w-0">
      <h2 className="text-base font-bold text-slate-900">{title}</h2>
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
    </div>
    {action}
  </div>
);

export const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}
  >
    {children}
  </div>
);

export const StatTile = ({ label, value, hint, tone = "neutral" }) => (
  <Card className="p-4">
    <div className="flex items-center gap-2">
      <Dot tone={tone} />
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
    </div>
    <p className="mt-1 text-2xl font-bold leading-tight text-slate-900">
      {value}
    </p>
    {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
  </Card>
);

/*
  Estado vazio com explicação.

  No CRM uma lista vazia é AMBÍGUA: pode ser trabalho feito (a fila zerou) ou
  regra que não encontrou ninguém. Por isso o texto é obrigatório — "nenhum
  resultado" sozinho não diz qual dos dois é.
*/
export const EmptyState = ({ title, description, icon: Icon }) => (
  <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
    {Icon ? (
      <span className="rounded-2xl bg-slate-100 p-3 text-slate-400">
        <Icon size={22} />
      </span>
    ) : null}
    <p className="text-sm font-semibold text-slate-700">{title}</p>
    {description ? (
      <p className="max-w-sm text-xs text-slate-500">{description}</p>
    ) : null}
  </div>
);

export const SkeletonRows = ({ rows = 4, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: rows }).map((_, index) => (
      <div
        key={`skeleton-${index}`}
        className="h-14 animate-pulse rounded-xl bg-slate-100"
      />
    ))}
  </div>
);

export const ErrorBlock = ({ message, onRetry }) => (
  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
    <p className="text-sm font-semibold text-rose-800">
      {message || "Não foi possível carregar os dados."}
    </p>
    {onRetry ? (
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 text-xs font-semibold text-rose-700 underline"
      >
        Tentar de novo
      </button>
    ) : null}
  </div>
);
